import { memo, useRef, useState } from 'react';
import { memoize } from 'proxy-memoize';
import { proxy } from 'valtio';
import { useSnapshot } from 'valtio';

import { ChessBoard, ChessDragPlaceData, ChessPieces } from '@exports/ChessBoard';
import { StrokedChessPieces } from '@exports/ChessBoard/ChessPieces';
import { useSetupBoard } from '@exports/ChessBoard/hooks/useSetupBoard';
import { FEN_EMPTY, FEN_EMPTY_WITH_CASTLING, FEN_STARTING_POSITION } from '@libs/chess/constants';
import { FenObject, parseFen } from '@libs/chess/parseFen';
import { PieceCode } from '@libs/chess/types';
import { isPlayableFen } from '@libs/chess/validateFen';
import { cx } from '@libs/classnames';
import { FUNC_PREVENT_DEFAULT } from '@libs/constants';
import { useDrag } from '@libs/hooks/useDragAndDrop';

import { BucketIcon, ResetIcon } from './Icons';
import { Button, Radio, RoundButton, MultilineInput, VerticalRadio } from './SimpleControls';
import { replaceFenColor } from './utils';

import styles from './PopupControls.module.css';

/*
    ____,-------------------------------,____
    \   |           Component           |   /
    /___|-------------------------------|___\
*/

const TURNS = [
  { title: 'Ход белых', value: 'w' as const },
  { title: 'Ход черных', value: 'b' as const },
];

const BLACK_PIECES: PieceCode[] = ['bK', 'bQ', 'bR', 'bN', 'bB', 'bP'];
const WHITE_PIECES: PieceCode[] = ['wK', 'wQ', 'wR', 'wN', 'wB', 'wP'];

export const PositionEditor = memo(function PositionEditor({
  fen,
  isMobile,
  onChange,
  onFenToggled,
  noValidate,
  hideFenInput,
}: {
  fen?: string;
  isMobile: boolean;
  onChange?: (fen?: string) => void;
  onFenToggled?: (isOpen: boolean) => void;
  noValidate?: boolean;
  hideFenInput?: boolean;
}) {
  const [store] = useState(() => new PositionEditorStore(fen, onChange));
  store.keepUpToDate(fen, onChange, onFenToggled);
  const snapshot = useSnapshot(store.state);

  const { dragging, hasWhiteKing, hasBlackKing, onDragChange, props } = useSetupBoard({
    fen: snapshot.fen,
    onChange,
    noValidate: noValidate,
  });

  return (
    <section className={styles.position_editor}>
      <ChessBoard orientation="white" theme="classic-lite" notation="none" {...props} />
      <div
        className={cx(styles.position_editor__pieces, styles.is_black, 'board-theme-classic-lite')}
      >
        {BLACK_PIECES.map((p) => (
          <ChessPiece
            key={p}
            piece={p}
            dragging={dragging === p}
            disabled={!noValidate && p === 'bK' && hasBlackKing}
            setDragging={onDragChange}
          />
        ))}
        <RoundButton onClick={store.onReset} disabled={snapshot.fen === FEN_STARTING_POSITION}>
          <ResetIcon />
        </RoundButton>
      </div>
      <div
        className={cx(styles.position_editor__pieces, styles.is_white, 'board-theme-classic-lite')}
      >
        {WHITE_PIECES.map((p) => (
          <ChessPiece
            key={p}
            piece={p}
            dragging={dragging === p}
            disabled={!noValidate && p === 'wK' && hasWhiteKing}
            setDragging={onDragChange}
          />
        ))}
        <RoundButton onClick={store.onClear} disabled={!snapshot.fen || snapshot.fen === FEN_EMPTY}>
          <BucketIcon />
        </RoundButton>
      </div>
      <div className={styles.position_editor__controls}>
        {!isMobile && (
          <VerticalRadio
            className={styles.position_editor__controls__color}
            centered
            options={TURNS}
            selected={snapshot.selectedColor}
            onChange={store.onChangeColor}
          />
        )}
        {isMobile && (
          <Radio options={TURNS} selected={snapshot.selectedColor} onChange={store.onChangeColor} />
        )}
        {snapshot.textOpened && (
          <MultilineInput
            className={styles.position_editor__controls__textarea}
            autoFocus
            onChange={store.onFenTextChange}
          />
        )}
        <div className={styles.position_editor__controls__buttons}>
          {!snapshot.textOpened && !hideFenInput && (
            <Button onClick={store.onFenTextOpen}>Ввести FEN</Button>
          )}
          {snapshot.textOpened && (
            <>
              {isMobile && <Button onClick={store.onFenTextClose}>Отменить</Button>}
              <Button
                onClick={store.onFenTextApply}
                disabled={!snapshot.textParsed || !snapshot.isValidText}
              >
                Применить
              </Button>
            </>
          )}
        </div>
      </div>
    </section>
  );
});

const ChessPiece = memo(function ChessPiece({
  piece,
  dragging,
  disabled,
  setDragging,
}: {
  piece: PieceCode;
  dragging: boolean;
  disabled?: boolean;
  setDragging: (d: PieceCode | null) => void;
}) {
  const ref = useRef(null);
  const refDrag = useRef(null);

  useDrag<ChessDragPlaceData | undefined>(ref, refDrag, {
    canDrag: () => true,
    onStart() {
      setDragging(piece);
      return { type: 'place', piece };
    },
    onStop() {
      setDragging(null);
    },
  });

  const key = piece[1] as keyof ChessPieces;
  const PieceComponent = StrokedChessPieces[key];

  return (
    <div className={styles.position_editor__pieces__piece} onContextMenu={FUNC_PREVENT_DEFAULT}>
      <div className={cx({ [styles.is_disabled]: disabled || dragging })} ref={ref}>
        <PieceComponent />
      </div>
      {dragging && (
        <div className={styles.position_editor__pieces__preview} ref={refDrag} inert>
          <PieceComponent />
        </div>
      )}
    </div>
  );
});

/*
    ____,-------------------------------,____
    \   |             Store             |   /
    /___|-------------------------------|___\
*/

class PositionEditorState {
  constructor(public fen?: string) {}
  textOpened = false;
  textFen = '';

  get fenParsed(): FenObject | null {
    return this._fenParsed(this);
  }
  private _fenParsed = memoize((snap: PositionEditorState) => parseFen(snap.fen));

  get textParsed(): FenObject | null {
    return this._textParsed(this);
  }
  private _textParsed = memoize((snap: PositionEditorState) => parseFen(snap.textFen));

  get isValidText() {
    return this._isValidText(this);
  }
  private _isValidText = memoize((snap: PositionEditorState) => {
    const result = isPlayableFen(snap.textFen);
    return result;
  });

  get selectedColor(): 'w' | 'b' {
    return this.fenParsed?.color === 'black' ? 'b' : 'w';
  }
}

class PositionEditorStore {
  constructor(
    fen?: string,
    private onChange?: (fen?: string) => void,
  ) {
    this.state = proxy(new PositionEditorState(fen));
  }

  state: PositionEditorState;
  private onFenToggled?: (isOpen: boolean) => void;

  keepUpToDate = (
    fen?: string,
    onChange?: (fen?: string) => void,
    onFenToggled?: (isOpen: boolean) => void,
  ) => {
    this.state.fen = fen;
    this.onChange = onChange;
    this.onFenToggled = onFenToggled;
  };

  onFenTextOpen = () => {
    this.state.textFen = '';
    this.state.textOpened = true;
    this.onFenToggled?.(true);
  };

  onFenTextClose = () => {
    this.state.textFen = '';
    this.state.textOpened = false;
    this.onFenToggled?.(false);
  };

  onFenTextChange = (fen: string) => {
    this.state.textFen = fen;
  };

  onFenTextApply = () => {
    this.state.textOpened = false;
    this.onFenToggled?.(false);
    this.onChange?.(this.state.textFen.trim());
    this.state.textFen = '';
  };

  onChangeColor = (o: { value: 'w' | 'b' }) => {
    if (!this.state.fen) {
      return;
    }

    this.onChange?.(replaceFenColor(this.state.fen, o.value));
  };

  onReset = () => {
    this.onChange?.(FEN_STARTING_POSITION);
  };

  onClear = () => {
    this.onChange?.(replaceFenColor(FEN_EMPTY_WITH_CASTLING, this.state.selectedColor));
  };
}
